import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle, Mail, CheckCircle2, ShieldCheck, ArrowUpRight,
  ArchiveRestore, User, Phone, MapPin, ChevronRight, History, ArrowRight,
  Clock, FileText, SearchCheck, HelpCircle
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import SEO from "@/components/SEO";

const SECTIONS = [
  { id: "matters", title: "1. Matters Covered", icon: AlertCircle },
  { id: "how-to-raise", title: "2. How to Raise a Grievance", icon: Mail },
  { id: "acknowledgement", title: "3. Acknowledgement", icon: CheckCircle2 },
  { id: "investigation", title: "4. Investigation & Resolution", icon: FileText },
  { id: "privacy", title: "5. Privacy Complaints", icon: ShieldCheck },
  { id: "escalation", title: "6. Escalation", icon: ArrowUpRight },
  { id: "records", title: "7. Record Keeping", icon: ArchiveRestore },
];

export default function GrievancePolicy() {
  const [activeSection, setActiveSection] = useState("matters");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 140;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const navOffset = 100;
      const top = el.getBoundingClientRect().top + window.pageYOffset - navOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-navy-900 antialiased" data-testid="grievance-policy-page">
      <SEO
        title="Grievance Redressal Policy | Biz360 Compliance"
        description="Official Grievance Redressal Policy for Biz360 Compliance. Learn how to raise issues, contact our designated Grievance Officer, and request escalations."
        canonical="/grievance-redressal-policy"
        breadcrumbs={[{ label: "Grievance Redressal Policy", href: "/grievance-redressal-policy" }]}
      />
      <ScrollProgress />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16 border-b border-slate-200">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="grievance-breadcrumb">
                <Link to="/" className="hover:text-navy-800 transition-colors">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-navy-700">Grievance Redressal Policy</span>
              </div>
            </Reveal>

            <div className="text-center">
              <Reveal delay={0.05}>
                <span className="inline-flex items-center rounded-full border border-gold-300/60 bg-gold-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-gold-700">
                  Customer Support & Escalations
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-900 md:text-5xl">
                  GRIEVANCE REDRESSAL POLICY
                </h1>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/80 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-sm border border-slate-200">
                  <History className="h-3.5 w-3.5 text-gold-600" />
                  <span>Last Updated: <strong className="text-navy-900">01/09/2026</strong></span>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-navy-900/10 bg-white/90 p-6 text-left shadow-sm backdrop-blur-sm">
                  <p className="text-base font-semibold text-navy-900">
                    Biz360 Compliance aims to provide clear and responsive support to clients and website users.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    This Grievance Redressal Policy explains how users may raise complaints relating to our website, services, payments, data handling or client portal.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Content Container */}
        <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            
            {/* Quick Navigation Sticky Sidebar */}
            <aside className="hidden lg:col-span-4 lg:block">
              <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm max-h-[calc(100vh-140px)] overflow-y-auto">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">
                  Table of Contents
                </h3>
                <nav className="space-y-1">
                  {SECTIONS.map((sec) => {
                    const SIcon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        onClick={() => scrollToSection(sec.id)}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-xs font-medium transition-all ${
                          isActive
                            ? "bg-navy-900 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                        }`}
                      >
                        <SIcon className={`h-4 w-4 shrink-0 ${isActive ? "text-gold-400" : "text-slate-400"}`} />
                        <span className="truncate">{sec.title}</span>
                      </button>
                    );
                  })}
                </nav>

                <div className="mt-6 border-t border-slate-100 pt-5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 px-2">
                    Related Legal Policies
                  </h4>
                  <div className="space-y-1.5 text-xs font-medium">
                    <Link
                      to="/terms"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Terms & Conditions</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/privacy-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Privacy Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/data-security-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Data Security Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/data-retention-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Data Retention Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/refund-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Refund & Cancellation Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/cookie-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Cookie Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Policy Content */}
            <div className="space-y-8 lg:col-span-8">

              {/* 1. Matters Covered */}
              <article id="matters" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <AlertCircle className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">1. Matters Covered</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  A grievance may relate to:
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Service delivery;",
                    "Delay;",
                    "Billing or payment;",
                    "Refund;",
                    "Website functionality;",
                    "Client portal;",
                    "Data privacy;",
                    "Unauthorised access;",
                    "Communication;",
                    "Other service-related concerns."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 rounded-lg bg-slate-50 p-3 border border-slate-100">
                      <span className="h-2 w-2 rounded-full bg-gold-500 shrink-0" />
                      <span className="text-xs font-semibold text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 2. How to Raise a Grievance */}
              <article id="how-to-raise" className="rounded-2xl border border-gold-300/70 bg-gradient-to-br from-white to-gold-50/15 p-6 md:p-8 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gold-600 text-white shadow-sm">
                    <Mail className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">2. How to Raise a Grievance</h2>
                </div>
                <p className="mt-3 text-sm text-slate-700">
                  Please direct all formal complaints and concerns to our designated Grievance Officer:
                </p>

                {/* Grievance Officer Card */}
                <div className="mt-5 space-y-4 rounded-xl border border-navy-900/10 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <User className="h-4 w-4 text-gold-600 shrink-0" />
                    <span>Grievance Officer / Contact Person: <strong className="text-navy-900">Narayan Bochare</strong></span>
                  </div>

                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <Mail className="h-4 w-4 text-gold-600 shrink-0" />
                    <span>Email: <a href="mailto:biz360compliance@gmail.com" className="font-semibold text-navy-900 hover:text-gold-600 transition-colors">biz360compliance@gmail.com</a></span>
                  </div>

                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <Phone className="h-4 w-4 text-gold-600 shrink-0" />
                    <span>Phone: <a href="tel:9067368357" className="font-semibold text-navy-900 hover:text-gold-600 transition-colors">9067368357</a></span>
                  </div>

                  <div className="flex items-start gap-3 text-sm text-slate-700">
                    <MapPin className="h-4 w-4 text-gold-600 shrink-0 mt-0.5" />
                    <span>Address: <strong className="text-navy-900">Office No. 70, 4th Floor, Patil Arcade, Above Kalonga Hotel, Khilare Road, near Teck Mahindra, Erandwane, Pune 411004</strong></span>
                  </div>
                </div>

                {/* Required Information */}
                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-navy-900">
                    Please provide the following details when submitting your grievance:
                  </h4>
                  <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-xs font-medium text-slate-700">
                    {[
                      "Name",
                      "Client/account details, where applicable",
                      "Relevant service",
                      "Invoice/reference number",
                      "Description of the issue",
                      "Supporting documents, if any"
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </article>

              {/* 3. Acknowledgement */}
              <article id="acknowledgement" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <CheckCircle2 className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">3. Acknowledgement</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  We will endeavour to acknowledge complaints within a reasonable period.
                </p>
                <p className="mt-3 text-xs font-semibold text-slate-600 bg-slate-100 p-3 rounded-xl">
                  Where additional information is required, we may request it from the complainant.
                </p>
              </article>

              {/* 4. Investigation & Resolution */}
              <article id="investigation" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <FileText className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">4. Investigation & Resolution</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  The grievance may be reviewed by the appropriate team or authorised person.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">
                  Depending on the nature of the matter, resolution may involve:
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Correction of an error;",
                    "Completion of pending work;",
                    "Clarification;",
                    "Refund review;",
                    "Account correction;",
                    "Security investigation; or",
                    "Other appropriate action."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 rounded-lg bg-slate-50 p-2.5 border border-slate-100 text-xs font-medium text-slate-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 5. Privacy Complaints */}
              <article id="privacy" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">5. Privacy Complaints</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Privacy or personal-data complaints will be handled in accordance with our{" "}
                  <Link to="/privacy-policy" className="text-navy-900 underline font-bold hover:text-gold-600 transition-colors">
                    Privacy Policy
                  </Link>{" "}
                  and applicable data-protection requirements.
                </p>
              </article>

              {/* 6. Escalation */}
              <article id="escalation" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ArrowUpRight className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">6. Escalation</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  If a grievance is not resolved satisfactorily, the complainant may request escalation to senior management using the contact details above.
                </p>
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-xs font-semibold text-slate-800">
                  Nothing in this Policy limits any statutory rights or remedies available under applicable law.
                </div>
              </article>

              {/* 7. Record Keeping */}
              <article id="records" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ArchiveRestore className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">7. Record Keeping</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Grievance records may be maintained for appropriate operational, legal, security and compliance purposes.
                </p>
              </article>

            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
